import React, { useState, useEffect, useMemo } from 'react';
import {
  Loader2, Search, AlertTriangle, CheckCircle2, RefreshCw,
  GraduationCap, Users, ShieldAlert, Save, Beaker, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  useLazyAnalyserEligibiliteClasseQuery,
  useEnregistrerChoixMutation,
  useSimulerRattrapageMutation,
} from '../../services/rattrapageApi';
import type {
  AnalyseEligibiliteEtudiant,
  UERattrapage,
  SimulationRattrapageResponse,
} from '../../services/rattrapageApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';

const fmt = (n: number | null | undefined): string => (n == null ? '—' : n.toFixed(2));

const ChoixRattrapage: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: classes = [] } = useGetClassesQuery({ actif: true });

  const [classeCode, setClasseCode] = useState('');
  const [semestre, setSemestre] = useState<1 | 2>(1);
  const [annee, setAnnee] = useState('');

  // Sélections en cours par étudiant : { etudiant_id: Set<matiere_id> }
  const [selections, setSelections] = useState<Record<number, Set<number>>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Simulation par étudiant
  const [simulations, setSimulations] = useState<Record<number, SimulationRattrapageResponse>>({});

  const [trigger, { data: analyse, isFetching, error }] = useLazyAnalyserEligibiliteClasseQuery();
  const [enregistrer, { isLoading: saving }] = useEnregistrerChoixMutation();
  const [simuler] = useSimulerRattrapageMutation();

  useEffect(() => {
    if (anneeActive?.libelle && !annee) setAnnee(anneeActive.libelle);
  }, [anneeActive, annee]);

  const handleConsulter = () => {
    if (!classeCode.trim() || !annee) return;
    trigger({
      classe_code: classeCode.trim().toUpperCase(),
      semestre,
      annee_academique: annee,
      concernes_seulement: true,
    });
  };

  // Initialiser les sélections avec les choix déjà enregistrés (deja_choisi)
  useEffect(() => {
    if (!analyse) return;
    const init: Record<number, Set<number>> = {};
    for (const e of analyse.etudiants) {
      const set = new Set<number>();
      for (const ue of e.ues) {
        for (const m of ue.matieres) {
          if (m.deja_choisi) set.add(m.matiere_id);
        }
      }
      init[e.etudiant_id] = set;
    }
    setSelections(init);
    setSimulations({});
  }, [analyse]);

  const toggleMatiere = (etudiantId: number, matiereId: number) => {
    setSelections((s) => {
      const set = new Set(s[etudiantId] || []);
      if (set.has(matiereId)) set.delete(matiereId);
      else set.add(matiereId);
      return { ...s, [etudiantId]: set };
    });
    // Invalide la simulation précédente
    setSimulations((sim) => {
      const next = { ...sim };
      delete next[etudiantId];
      return next;
    });
  };

  const toggleExpand = (id: number) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSimuler = async (etu: AnalyseEligibiliteEtudiant) => {
    const matieres_ids = Array.from(selections[etu.etudiant_id] || []);
    try {
      const res = await simuler({
        etudiant_id: etu.etudiant_id,
        semestre,
        annee_academique: annee,
        matieres_ids,
        note_simulee: 10,
      }).unwrap();
      setSimulations((s) => ({ ...s, [etu.etudiant_id]: res }));
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur de simulation');
    }
  };

  const handleEnregistrer = async (etu: AnalyseEligibiliteEtudiant) => {
    const matieres_ids = Array.from(selections[etu.etudiant_id] || []);
    try {
      const res = await enregistrer({
        etudiant_id: etu.etudiant_id,
        semestre,
        annee_academique: annee,
        matieres_ids,
      }).unwrap();
      const msg = `${res.nb_crees} créé(s), ${res.nb_reactives} réactivé(s), ${res.nb_annules} annulé(s)`;
      alert(`Choix enregistrés : ${msg}`);
      handleConsulter(); // refresh
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-6 flex items-center gap-3">
        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
          <GraduationCap size={24} className="text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Choix des matières à rattraper
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Sélectionnez les matières que chaque étudiant ajourné devra repasser en session 2
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Classe</label>
            <select
              value={classeCode}
              onChange={(e) => setClasseCode(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">— Choisir —</option>
              {(classes as any[]).map((c: any) => (
                <option key={c.id} value={c.code}>{c.code}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
            <select
              value={semestre}
              onChange={(e) => setSemestre(Number(e.target.value) as 1 | 2)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année académique</label>
            <input
              type="text"
              value={annee}
              onChange={(e) => setAnnee(e.target.value)}
              placeholder="2025-2026"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <button
            onClick={handleConsulter}
            disabled={isFetching || !classeCode || !annee}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
          >
            {isFetching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Analyser
          </button>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 mt-0.5" />
          <span className="text-xs text-red-700 dark:text-red-300">
            Impossible de charger l'analyse. Vérifiez les paramètres.
          </span>
        </div>
      )}

      {/* Résultats */}
      {analyse && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <StatCard label="Effectif classe" value={analyse.total_classe} icon={<Users size={14} />} color="blue" />
            <StatCard label="Concernés par rattrapage" value={analyse.total_concernes} icon={<ShieldAlert size={14} />} color="orange" />
            <StatCard label="Validés sans rattrapage" value={analyse.total_classe - analyse.total_concernes} icon={<CheckCircle2 size={14} />} color="green" />
          </div>

          {analyse.etudiants.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-700/50 p-12 text-center">
              <CheckCircle2 size={40} className="mx-auto text-green-400 mb-3" />
              <p className="text-base font-medium text-green-700 dark:text-green-300">
                Aucun étudiant à rattraper
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Tous les étudiants de {analyse.classe_code} ont validé S{analyse.semestre}.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {analyse.etudiants.map((etu) => (
                <EtudiantCard
                  key={etu.etudiant_id}
                  etudiant={etu}
                  selection={selections[etu.etudiant_id] || new Set()}
                  simulation={simulations[etu.etudiant_id]}
                  expanded={expanded.has(etu.etudiant_id)}
                  saving={saving}
                  onToggleExpand={() => toggleExpand(etu.etudiant_id)}
                  onToggleMatiere={(mid) => toggleMatiere(etu.etudiant_id, mid)}
                  onSimuler={() => handleSimuler(etu)}
                  onEnregistrer={() => handleEnregistrer(etu)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number; icon: React.ReactNode; color: string }> = ({
  label, value, icon, color,
}) => {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    orange: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
    green: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
  };
  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

const EtudiantCard: React.FC<{
  etudiant: AnalyseEligibiliteEtudiant;
  selection: Set<number>;
  simulation?: SimulationRattrapageResponse;
  expanded: boolean;
  saving: boolean;
  onToggleExpand: () => void;
  onToggleMatiere: (matiereId: number) => void;
  onSimuler: () => void;
  onEnregistrer: () => void;
}> = ({ etudiant, selection, simulation, expanded, saving, onToggleExpand, onToggleMatiere, onSimuler, onEnregistrer }) => {
  const ueAObligRatt = etudiant.ues.filter((u) => u.obligatoire_a_rattraper);
  const nbSelectionne = selection.size;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
          <div className="text-left">
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {etudiant.nom.toUpperCase()} {etudiant.prenoms}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {etudiant.matricule} · Moyenne sem : <strong>{fmt(etudiant.moyenne_semestre)}</strong>
              {' · '}
              {etudiant.semestre_valide ? (
                <span className="text-green-600 dark:text-green-400">Semestre validé</span>
              ) : (
                <span className="text-red-600 dark:text-red-400">Ajourné</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {ueAObligRatt.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <ShieldAlert size={10} /> {ueAObligRatt.length} UE éliminatoire{ueAObligRatt.length > 1 ? 's' : ''}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            {nbSelectionne} matière{nbSelectionne > 1 ? 's' : ''} sélectionnée{nbSelectionne > 1 ? 's' : ''}
          </span>
        </div>
      </button>

      {/* Contenu déplié */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
          {etudiant.ues.map((ue) => (
            <UEBlock
              key={ue.ue_id}
              ue={ue}
              selection={selection}
              onToggleMatiere={onToggleMatiere}
              seuilElim={etudiant.seuils.seuil_eliminatoire_ue}
            />
          ))}

          {/* Simulation result */}
          {simulation && (
            <SimulationBox simulation={simulation} />
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onSimuler}
              disabled={nbSelectionne === 0}
              className="px-3 py-1.5 text-xs font-medium border border-purple-300 dark:border-purple-700/50 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
            >
              <Beaker size={12} />
              Simuler (note examen = 10)
            </button>
            <button
              onClick={onEnregistrer}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Enregistrer les choix
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const UEBlock: React.FC<{
  ue: UERattrapage;
  selection: Set<number>;
  onToggleMatiere: (id: number) => void;
  seuilElim: number;
}> = ({ ue, selection, onToggleMatiere, seuilElim }) => {
  const cls =
    ue.obligatoire_a_rattraper
      ? 'border-red-300 dark:border-red-700/50 bg-red-50 dark:bg-red-900/10'
      : ue.recommande_rattraper
      ? 'border-orange-300 dark:border-orange-700/50 bg-orange-50 dark:bg-orange-900/10'
      : 'border-gray-200 dark:border-gray-700';
  return (
    <div className={`rounded-lg border ${cls} overflow-hidden`}>
      <div className="px-3 py-2 flex items-center justify-between bg-white/60 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-gray-700 dark:text-gray-300">{ue.code_ue}</span>
          <span className="text-sm text-gray-700 dark:text-gray-300">{ue.nom_ue}</span>
          {ue.obligatoire_a_rattraper && (
            <span className="text-[10px] font-bold text-red-700 dark:text-red-300 px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 rounded">
              ÉLIMINATOIRE
            </span>
          )}
          {!ue.validee && !ue.obligatoire_a_rattraper && (
            <span className="text-[10px] font-bold text-orange-700 dark:text-orange-300 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 rounded">
              NON VALIDÉE
            </span>
          )}
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-400">
          Moy. UE : <strong className={ue.eliminatoire ? 'text-red-600 dark:text-red-400' : ''}>{fmt(ue.moyenne_ue)}</strong>
          {ue.eliminatoire && <span className="ml-1 text-[10px]">(&lt; {seuilElim})</span>}
        </div>
      </div>

      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700/30">
          <tr>
            <th className="px-3 py-1.5 w-8"></th>
            <th className="px-3 py-1.5 text-left text-gray-500 dark:text-gray-400 font-semibold">Matière</th>
            <th className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400 font-semibold">Devoir</th>
            <th className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400 font-semibold">Examen</th>
            <th className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400 font-semibold">Moy. ECUE</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {ue.matieres.map((m) => {
            const checked = selection.has(m.matiere_id);
            return (
              <tr key={m.matiere_id} className={checked ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}>
                <td className="px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleMatiere(m.matiere_id)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </td>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">
                  <span className="font-mono text-gray-500 mr-1">{m.code_matiere}</span>
                  {m.nom_matiere}
                  {m.eliminatoire_ecue && (
                    <span className="ml-1 text-[10px] font-bold text-red-700 dark:text-red-300 px-1 py-0.5 bg-red-100 dark:bg-red-900/30 rounded">
                      Élim
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center font-mono">{fmt(m.note_devoir)}</td>
                <td className="px-3 py-1.5 text-center font-mono">
                  {m.absent_examen ? <span className="text-red-500">AB</span> : fmt(m.note_examen)}
                </td>
                <td className="px-3 py-1.5 text-center font-mono font-semibold">{fmt(m.moyenne_ecue)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SimulationBox: React.FC<{ simulation: SimulationRattrapageResponse }> = ({ simulation }) => {
  const ok = simulation.choix_efficaces || (simulation.semestre_valide_apres ?? false);
  const colorCls = ok
    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700/50 text-green-800 dark:text-green-300'
    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/50 text-amber-800 dark:text-amber-300';
  return (
    <div className={`rounded-lg border p-3 text-xs ${colorCls}`}>
      <div className="flex items-center gap-1.5 font-semibold mb-1.5">
        <Beaker size={12} />
        Simulation avec note d'examen = {simulation.note_examen_simulee}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <div>
          <span className="opacity-70">Moyenne semestre :</span>{' '}
          <strong>{fmt(simulation.moyenne_semestre_avant)}</strong> →{' '}
          <strong>{fmt(simulation.moyenne_semestre_apres)}</strong>
        </div>
        <div>
          <span className="opacity-70">Statut semestre :</span>{' '}
          {simulation.semestre_valide_apres == null ? (
            <strong>—</strong>
          ) : simulation.semestre_valide_apres ? (
            <strong className="text-green-700 dark:text-green-300">VALIDÉ</strong>
          ) : (
            <strong className="text-red-700 dark:text-red-300">AJOURNÉ</strong>
          )}
        </div>
      </div>
      {simulation.nb_ue_restees_eliminatoires > 0 ? (
        <div className="flex items-start gap-1">
          <AlertTriangle size={12} className="mt-0.5" />
          <span>
            UE encore éliminatoire(s) : <strong>{simulation.ue_restees_eliminatoires.join(', ')}</strong>
            {' — '}augmentez la note simulée ou ajoutez d'autres matières.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <CheckCircle2 size={12} />
          <span>Tous les seuils éliminatoires sont levés avec ces choix.</span>
        </div>
      )}
    </div>
  );
};

export default ChoixRattrapage;
